import React, { useState } from "react";
import {
  Download,
  Calendar,
  Filter,
  FileText,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
  exportSurveyExcel,
  getExportableSurveys,
  getExportHistory,
} from "../services/exports";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Header from "./Header";

export default function Export() {
  const { user, translations, token } = useAuth();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    surveyId: "",
    startDate: "",
    endDate: "",
    language: "es" as "es" | "ca" | "en" | "ar",
  });
  const [isExporting, setIsExporting] = useState(false);

  const {
    data: availableSurveys = [],
    isLoading,
  } = useQuery({
    queryKey: [
      "exportable-surveys",
      formData.language,
      formData.startDate,
      formData.endDate,
    ],
    queryFn: () =>
      getExportableSurveys(token!, {
        language: formData.language,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
      }),
    enabled: !!token,
  });

  const {
    data: history = [],
    isLoading: isLoadingHistory,
    error: errorHistory,
  } = useQuery({
    queryKey: ["export-history", formData.language],
    queryFn: () =>
      getExportHistory(token!, { language: formData.language, limit: 10 }),
    enabled: !!token,
  });

  // // Mock surveys for selection
  // const availableSurveys = [
  //   { id: "1", title: "Impacto Educativo Q1 2024", responses: 156 },
  //   { id: "2", title: "Bienestar Familiar", responses: 89 },
  //   { id: "3", title: "Desarrollo Comunitario", responses: 234 },
  // ];

  const handleExport = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsExporting(true);
    try {
      // Obtener la encuesta seleccionada para usar su nombre
      const currentSurvey = availableSurveys.find(
        (s) => s.id === formData.surveyId
      );
      
      const blob = await exportSurveyExcel(token!, {
        surveyId: formData.surveyId,
        startDateUtc: formData.startDate
          ? new Date(formData.startDate).toISOString()
          : null,
        endDateUtc: formData.endDate
          ? new Date(formData.endDate).toISOString()
          : null,
        language: formData.language,
      });
      
      // Generar nombre del archivo dinámicamente
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      const dateStr = `${day} ${month} ${year}`;
      
      // Obtener el nombre de la encuesta tal cual
      const surveyTitle = currentSurvey?.title || "encuesta";
      const cleanTitle = surveyTitle.trim();
      
      const filename = `Exportación datos encuesta ${cleanTitle} ${dateStr}.xlsx`;
      
      // Crear un enlace temporal para descargar el archivo
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Liberar la URL del objeto
      window.URL.revokeObjectURL(url);
      
      queryClient.invalidateQueries({ queryKey: ["exportable-surveys"] });
      queryClient.invalidateQueries({ queryKey: ["export-history"] });
    } finally {
      setIsExporting(false);
    }
  };

  const selectedSurvey = availableSurveys.find(
    (s) => s.id === formData.surveyId
  );
  const fmtDateTime = (iso: string) =>
    new Date(iso).toLocaleString(
      formData.language === "ca" ? "ca-ES" : "es-ES",
      {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <Header>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {translations.downloadData}
          </h1>
          <p className="text-gray-600 mt-1">
            {translations.downloadData}
          </p>
        </div>
      </Header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Export Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <form onSubmit={handleExport} className="p-6 space-y-6">
              <div className="flex items-center space-x-2 mb-4">
                <Download className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  {translations.exportData}
                </h2>
              </div>

              {/* Survey Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {translations.selectSurvey} *
                </label>
                <select
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.surveyId}
                  onChange={(e) =>
                    setFormData({ ...formData, surveyId: e.target.value })
                  }
                  disabled={isLoading}
                >
                  <option value="">
                    {isLoading ? "Cargando..." : "Selecciona una encuesta"}
                  </option>
                  {availableSurveys.map((survey) => (
                    <option key={survey.id} value={survey.id}>
                      {survey.title} ({survey.responses} respuestas)
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {translations.dateRange}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Fecha inicio
                    </label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.startDate}
                      onChange={(e) =>
                        setFormData({ ...formData, startDate: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Fecha fin
                    </label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.endDate}
                      onChange={(e) =>
                        setFormData({ ...formData, endDate: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {translations.language || "Idioma de exportación"}
                </label>
                <select
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.language}
                  onChange={(e) =>
                    setFormData({ ...formData, language: e.target.value as "es" | "ca" | "en" | "ar" })
                  }
                >
                  <option value="es">{translations.spanish || "Español"}</option>
                  <option value="ca">{translations.catalan || "Català"}</option>
                  <option value="en">{translations.english || "English"}</option>
                  <option value="ar">{translations.arabic || "Árabe"}</option>
                </select>
              </div>

              {/* Submit Button */}
              <div className="pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={!formData.surveyId || isExporting}
                  className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Exportando...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      {translations.exportToExcel}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Export Info */}
        <div className="space-y-6">
          {/* Selection Summary */}
          {selectedSurvey && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Resumen de exportación
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Encuesta seleccionada</p>
                  <p className="font-medium">{selectedSurvey.title}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    Respuestas disponibles
                  </p>
                  <p className="font-medium">{selectedSurvey.responses}</p>
                </div>
                {(formData.startDate || formData.endDate) && (
                  <div>
                    <p className="text-sm text-gray-500">Rango de fechas</p>
                    <p className="font-medium">
                      {formData.startDate
                        ? new Date(formData.startDate).toLocaleDateString()
                        : "Sin inicio"}{" "}
                      -{" "}
                      {formData.endDate
                        ? new Date(formData.endDate).toLocaleDateString()
                        : "Sin fin"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Export Guidelines */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <Filter className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Consideraciones importantes
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Los datos se exportan de forma anónima</li>
                    <li>
                      Solo verás respuestas de tu{" "}
                      {user?.role === "Administrator"
                        ? "plataforma"
                        : "organización"}
                    </li>
                    <li>
                      El archivo incluirá todas las preguntas y respuestas
                    </li>
                    <li>
                      Los datos se descargan en formato CSV compatible con Excel
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Export History */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Exportaciones recientes
            </h3>

            {isLoadingHistory ? (
              <div className="space-y-3">
                <div className="animate-pulse h-10 bg-gray-100 rounded" />
                <div className="animate-pulse h-10 bg-gray-100 rounded" />
                <div className="animate-pulse h-10 bg-gray-100 rounded" />
              </div>
            ) : errorHistory ? (
              <p className="text-sm text-red-600">
                No se pudo cargar el historial.
              </p>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-500">
                Sin exportaciones todavía.
              </p>
            ) : (
              <div className="space-y-3">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                  >
                    <div>
                      <p className="text-sm font-medium">{item.surveyTitle}</p>
                      <p className="text-xs text-gray-500">
                        {fmtDateTime(item.requestedAtUtc)}
                      </p>
                      <p className="text-[11px] text-gray-500">
                        {item.respondentType || "all"} ·{" "}
                        {item.language.toUpperCase()} · {item.rows} filas
                      </p>
                    </div>
                    <span className="text-xs text-green-600 font-medium">
                      Completado
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
